import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Icon, Panel, Text, Toggle } from '../../../../components';
import { firstErrorMessage, firstWarningMessage, issuesFor } from '../../issues-by-field';
import { setRouteCanonical, setRouteFlag } from '../../seo-document';
import type { SiteSeoDocument, SiteSeoIssue, SiteSeoRoute } from '../../seo-types';
import { validateCanonical } from '../../seo-validation';
import {
  Body,
  Chevron,
  Disclosure,
  ErrorText,
  FieldBlock,
  HintText,
  ToggleLabel,
  ToggleRow,
} from './AdvancedPanel.styles';

export type AdvancedPanelProps = {
  draft: SiteSeoDocument;
  route: SiteSeoRoute;
  /** The site's host, which a canonical must be on. */
  siteHost: string;
  /** A footer wildcard: nothing here applies to a group of paths. */
  disabled: boolean;
  /** The server's issues; the noindex and canonical show their first error as is. */
  issues: SiteSeoIssue[];
  onChange: (update: (draft: SiteSeoDocument) => SiteSeoDocument) => void;
};

/**
 * Advanced, collapsed by default (open while the server reports an error in it): "Hide from Google (noindex)" and,
 * on an exact route only, the canonical URL ("auto" when empty) with the hub's hint when it is not an https address
 * on the site's host.
 */
export const AdvancedPanel = ({
  route,
  siteHost,
  disabled,
  issues,
  onChange,
}: AdvancedPanelProps): React.ReactElement => {
  const { t } = useTranslation();
  const { path } = route;
  const exact = route.match === 'exact';
  const noindexIssues = issuesFor(issues, path, 'noindex');
  const noindexError = firstErrorMessage(noindexIssues);
  const noindexWarning = noindexError ? undefined : firstWarningMessage(noindexIssues);
  const canonicalIssues = exact ? issuesFor(issues, path, 'canonical') : [];
  const canonicalError = firstErrorMessage(canonicalIssues);
  const canonicalWarning = canonicalError ? undefined : firstWarningMessage(canonicalIssues);
  // The owner's choice wins once made; until then the panel opens itself to show an error in it.
  const [chosen, setChosen] = React.useState<boolean | null>(null);
  const open = chosen ?? (noindexError !== undefined || canonicalError !== undefined);
  const canonical = route.canonical ?? '';
  const canonicalHint = validateCanonical(canonical, siteHost) !== null;

  const toggleNoindex = (): void => {
    if (disabled) return;
    onChange((d) => setRouteFlag(d, path, 'noindex', route.noindex !== true));
  };
  const setCanonical = (value: string): void => {
    if (disabled || !exact) return;
    onChange((d) => setRouteCanonical(d, path, value));
  };

  return (
    <Panel>
      <Disclosure type="button" aria-expanded={open} onClick={() => setChosen(!open)}>
        <Chevron $open={open} aria-hidden="true">
          <Icon name="chevron-down" size={16} />
        </Chevron>
        <Text text="Advanced" tag="span" />
      </Disclosure>
      {open ? (
        <Body>
          <FieldBlock data-field="noindex" data-tone={noindexWarning ? 'warning' : undefined}>
            <ToggleRow $disabled={disabled}>
              <ToggleLabel text="Hide from Google (noindex)" tag="span" />
              <Toggle
                on={route.noindex === true}
                label="Hide from Google (noindex)"
                disabled={disabled}
                onToggle={toggleNoindex}
              />
            </ToggleRow>
            {noindexError ? <ErrorText text={noindexError} raw tag="span" /> : null}
            {noindexWarning ? <HintText text={noindexWarning} raw tag="span" /> : null}
          </FieldBlock>
          {exact ? (
            <FieldBlock data-field="canonical" data-tone={canonicalWarning ? 'warning' : undefined}>
              <Field
                label={t('Canonical URL')}
                value={canonical}
                onChange={setCanonical}
                placeholder={t('auto')}
                disabled={disabled}
              />
              {canonicalHint ? (
                <HintText text="Use a full https address on {{host}}" placeholders={{ host: siteHost }} tag="span" />
              ) : null}
              {canonicalError ? <ErrorText text={canonicalError} raw tag="span" /> : null}
              {canonicalWarning ? <HintText text={canonicalWarning} raw tag="span" /> : null}
            </FieldBlock>
          ) : null}
        </Body>
      ) : null}
    </Panel>
  );
};

export default AdvancedPanel;
